import React, { useEffect, useRef, useState, useCallback } from 'react'
import { C, muteColor } from '../theme'

// Aged-cartography palette: warm sepias/muted greens for land, a faded blue-grey sea.
// Adopted from the Campaign 2 Tracker's terrain fills (RoadStrongholdSource ↔ TerrainSource).
const TERRAIN_COLORS = {
  Pla: '#d9ead3', G: '#68d9c4', Hl: '#bf9000', LtF: '#38761d', HvF: '#274e13',
  Mt: '#7f6000', Des: '#fff2cc', Lake: '#3c78d8', Cst: '#a4c2f4', Ocean: '#3c78d8',
  // land-use values from current_terrain (the final/displayed terrain)
  Fld: '#fbf266', City: '#b7b7b7',
}
const DEFAULT_TERRAIN = '#bdb293'

// Movement classes mirror the engine (terrain.js): a road overrides terrain to cost 1,
// so roaded tiles are never marked rough/blocked. Used by the "Faction only" overlay.
const COST2_TERRAIN = new Set(['Hl', 'LtF', 'HvF'])   // 2-movement-cost → faint diagonals
const BLOCK_TERRAIN = new Set(['Mt'])                  // impassable land → cross-hatch
const WATER_TERRAIN = new Set(['Lake', 'Cst', 'Ocean', 'water'])
// In Faction-only mode, unowned land reads as neutral parchment (water keeps its sea tone).
const NEUTRAL_LAND = '#d7c9a4'

export const MAP_MODES = [
  { id: 'borders', label: 'Borders' },
  { id: 'combined', label: 'Combined' },
  { id: 'faction', label: 'Faction' },
  { id: 'terrain', label: 'Terrain' },
]

// --- Starfield backdrop (replaces the flat sea behind the board) ---------------
// Seeded so the field is stable across pan/zoom redraws; rendered once to an
// offscreen canvas and blitted each frame (cheap, and avoids twinkle/jitter).
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const STAR_TINTS = ['#ffffff', '#dfe8ff', '#cdd8ff', '#ffe7c4', '#ffd8e6']
const HALO_TINTS = ['138,110,220', '96,140,235', '170,120,210', '120,180,230'] // purples/blues

function buildStarfield(width, height, dpr) {
  const off = document.createElement('canvas')
  off.width = width; off.height = height
  const ctx = off.getContext('2d')
  const rng = mulberry32(0x7a2c91)

  // deep-space base wash
  const bg = ctx.createLinearGradient(0, 0, width, height)
  bg.addColorStop(0, '#05060e'); bg.addColorStop(0.5, '#080612'); bg.addColorStop(1, '#04050b')
  ctx.fillStyle = bg; ctx.fillRect(0, 0, width, height)

  // a few faint nebula clouds for depth
  for (let i = 0; i < 3; i++) {
    const nx = rng() * width, ny = rng() * height
    const nr = (0.25 + rng() * 0.3) * Math.max(width, height)
    const tint = HALO_TINTS[(rng() * HALO_TINTS.length) | 0]
    const g = ctx.createRadialGradient(nx, ny, 0, nx, ny, nr)
    g.addColorStop(0, `rgba(${tint},0.10)`)
    g.addColorStop(0.5, `rgba(${tint},0.04)`)
    g.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = g; ctx.fillRect(0, 0, width, height)
  }

  // stars: mostly faint dots, a few bright twinkles, some haloed
  const count = Math.round((width * height) / (5200 * dpr))
  for (let i = 0; i < count; i++) {
    const x = rng() * width, y = rng() * height
    const mag = rng()
    const r = (0.4 + mag * mag * 1.8) * dpr
    const color = STAR_TINTS[(rng() * STAR_TINTS.length) | 0]
    const alpha = 0.45 + rng() * 0.55
    const haloed = rng() < 0.16
    const sparkle = rng() < 0.10 || mag > 0.93

    if (haloed) {
      const tint = HALO_TINTS[(rng() * HALO_TINTS.length) | 0]
      const hr = r * (4 + rng() * 4)
      const g = ctx.createRadialGradient(x, y, 0, x, y, hr)
      g.addColorStop(0, `rgba(${tint},0.35)`)
      g.addColorStop(1, `rgba(${tint},0)`)
      ctx.fillStyle = g
      ctx.beginPath(); ctx.arc(x, y, hr, 0, Math.PI * 2); ctx.fill()
    }

    ctx.fillStyle = color
    if (sparkle) {
      const spike = r * (3 + rng() * 3)
      ctx.save()
      ctx.strokeStyle = color
      ctx.lineWidth = Math.max(0.5, r * 0.5)
      ctx.lineCap = 'round'
      ctx.globalAlpha = alpha * 0.7
      ctx.beginPath()
      ctx.moveTo(x - spike, y); ctx.lineTo(x + spike, y)
      ctx.moveTo(x, y - spike); ctx.lineTo(x, y + spike)
      ctx.stroke()
      ctx.restore()
    }
    ctx.globalAlpha = alpha
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
  }
  ctx.globalAlpha = 1
  return off
}

const tkey = (r, c) => r * 10000 + c

export default function CampaignMap({ map, armies = [], caravans = [], campFollowers = [], supplyBases = [], strongholds = [], sieges = [], selectedArmyId = null, plannedPath = [], onTileClick }) {
  const canvasRef = useRef(null)
  const view = useRef({ scale: 8, offsetX: 0, offsetY: 0, initialized: false })
  const drag = useRef(null)
  const starfield = useRef(null)
  const [mapMode, setMapMode] = useState(() => localStorage.getItem('tacitus.mapMode') || 'borders')
  // keep latest props for the draw loop
  const props = useRef({})
  props.current = { map, armies, caravans, campFollowers, supplyBases, strongholds, sieges, selectedArmyId, plannedPath, mapMode }

  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const { width, height } = canvas
    const v = view.current
    const { map, armies, caravans, campFollowers, supplyBases, strongholds, sieges, selectedArmyId, plannedPath, mapMode } = props.current
    const s = v.scale
    const cell = Math.ceil(s) + 0.5

    // space backdrop — built once per canvas size, then blitted
    const dpr = window.devicePixelRatio || 1
    let sf = starfield.current
    if (!sf || sf.width !== width || sf.height !== height) {
      sf = buildStarfield(width, height, dpr)
      starfield.current = sf
    }
    ctx.drawImage(sf, 0, 0)

    const px = (col) => v.offsetX + (col - 1) * s
    const py = (row) => v.offsetY + (row - 1) * s

    // precompute muted heraldic tint per faction (avoids per-tile color math on redraw)
    const tintByFaction = {}
    for (const f of map.factions) tintByFaction[f.id] = muteColor(f.color)

    // index road tiles (and the settlements roads tie into) for the road network
    const roadSet = new Set()
    const settleSet = new Set() // cities/forts — endpoints a road can run into
    const roadTiles = []
    const labelTiles = []       // named settlements — drawn as text on top of everything
    const ownerByKey = mapMode === 'borders' ? new Map() : null // for frontier-stripe lookup
    for (const t of map.tiles) {
      const [row, col, , controllerId, isCity, road, isFort, name] = t
      if (road) { roadSet.add(tkey(row, col)); roadTiles.push(t) }
      if (isCity || isFort) settleSet.add(tkey(row, col))
      if (name) labelTiles.push([row, col, name])
      if (ownerByKey && controllerId) ownerByKey.set(tkey(row, col), controllerId)
    }

    for (const [row, col, terrain, controllerId, isCity, road, isFort] of map.tiles) {
      const x = px(col), y = py(row)
      if (x + s < 0 || y + s < 0 || x > width || y > height) continue

      // base fill — terrain underlies every mode except "faction" (which paints ownership solid)
      if (mapMode === 'faction') {
        ctx.fillStyle = controllerId
          ? (tintByFaction[controllerId] || C.ink)
          : (WATER_TERRAIN.has(terrain) ? (TERRAIN_COLORS[terrain] || DEFAULT_TERRAIN) : NEUTRAL_LAND)
        ctx.fillRect(x, y, cell, cell)
        // terrain difficulty hatch (only here, where terrain colour is hidden); roads cost 1 → no hatch
        if (s >= 4 && !road && !WATER_TERRAIN.has(terrain)) {
          const block = BLOCK_TERRAIN.has(terrain), rough = COST2_TERRAIN.has(terrain)
          if (block || rough) {
            ctx.save()
            ctx.beginPath(); ctx.rect(x, y, cell, cell); ctx.clip()
            ctx.strokeStyle = block ? 'rgba(44,35,22,0.50)' : 'rgba(44,35,22,0.26)'
            ctx.lineWidth = Math.max(0.5, s * 0.045)
            ctx.beginPath()
            const step = Math.max(3, s / 3.5)
            for (let d = -cell; d < cell; d += step) {
              ctx.moveTo(x + d, y + cell); ctx.lineTo(x + d + cell, y)       // "/" diagonals
              if (block) { ctx.moveTo(x + d, y); ctx.lineTo(x + d + cell, y + cell) } // cross-hatch
            }
            ctx.stroke()
            ctx.restore()
          }
        }
      } else {
        ctx.fillStyle = TERRAIN_COLORS[terrain] || DEFAULT_TERRAIN
        ctx.fillRect(x, y, cell, cell)
        if (mapMode === 'combined' && controllerId) {
          ctx.fillStyle = tintByFaction[controllerId] || C.ink
          ctx.globalAlpha = 0.30; ctx.fillRect(x, y, cell, cell); ctx.globalAlpha = 1
        }
      }
      if (isCity && s >= 4) {
        ctx.fillStyle = C.paperRaised; ctx.strokeStyle = C.ink; ctx.lineWidth = 1.2
        ctx.beginPath(); ctx.arc(x + s / 2, y + s / 2, Math.max(1.5, s * 0.28), 0, Math.PI * 2); ctx.fill(); ctx.stroke()
      }
      if (isFort && s >= 4) {
        const w = Math.max(2, s * 0.42), o = (s - w) / 2
        ctx.fillStyle = C.inkSoft; ctx.strokeStyle = C.paperRaised; ctx.lineWidth = 1
        ctx.fillRect(x + o, y + o, w, w); ctx.strokeRect(x + o, y + o, w, w)
      }
    }

    // roads — thin lines linking each road tile to the tiles it connects to
    if (roadTiles.length) {
      ctx.strokeStyle = '#6f5232'
      ctx.lineWidth = Math.max(0.6, s * 0.16)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.globalAlpha = 0.85
      ctx.beginPath()
      // forward-half neighbour set dedupes road↔road segments; settlements (non-road
      // endpoints) are linked in any direction since they never iterate as roads.
      const DIRS = [[0, 1], [1, 1], [1, 0], [1, -1], [0, -1], [-1, -1], [-1, 0], [-1, 1]]
      for (const [row, col] of roadTiles) {
        const cx = px(col) + s / 2, cy = py(row) + s / 2
        if (cx < -s || cy < -s || cx > width + s || cy > height + s) continue
        for (const [dr, dc] of DIRS) {
          const nr = row + dr, nc = col + dc
          if (roadSet.has(tkey(nr, nc))) {
            const forward = (dr === 1) || (dr === 0 && dc === 1)
            if (!forward) continue
            // drop a redundant diagonal that just cuts the corner of an L of roads
            if (dr !== 0 && dc !== 0 && (roadSet.has(tkey(row + dr, col)) || roadSet.has(tkey(row, col + dc)))) continue
          } else if (!settleSet.has(tkey(nr, nc))) {
            continue
          }
          ctx.moveTo(cx, cy); ctx.lineTo(px(nc) + s / 2, py(nr) + s / 2)
        }
      }
      ctx.stroke()
      ctx.globalAlpha = 1
    }

    // frontier stripes (Borders mode) — a faction-coloured band on each edge of an owned
    // tile that meets a differently-owned (or unowned) orthogonal neighbour. Draws clean
    // political boundary lines while leaving terrain fully readable underneath.
    if (ownerByKey) {
      const thick = Math.max(1, s * 0.15)   // ~25% thinner than the first pass
      const t2 = thick                        // corner-square size
      for (const [row, col, , controllerId] of map.tiles) {
        if (!controllerId) continue
        const x = px(col), y = py(row)
        if (x + s < 0 || y + s < 0 || x > width || y > height) continue
        const diff = (r, c) => ownerByKey.get(tkey(r, c)) !== controllerId
        ctx.fillStyle = tintByFaction[controllerId] || C.ink
        // edge stripes on sides facing a different/unowned neighbour (no overhang)
        if (diff(row - 1, col)) ctx.fillRect(x, y, cell, thick)                 // top
        if (diff(row + 1, col)) ctx.fillRect(x, y + cell - thick, cell, thick)  // bottom
        if (diff(row, col - 1)) ctx.fillRect(x, y, thick, cell)                 // left
        if (diff(row, col + 1)) ctx.fillRect(x + cell - thick, y, thick, cell)  // right
        // inside corner-square where the diagonal neighbour differs — bridges cross-tile
        // (concave) corners so the boundary turns cleanly, without overhanging the tile
        if (diff(row - 1, col - 1)) ctx.fillRect(x, y, t2, t2)                                 // top-left
        if (diff(row - 1, col + 1)) ctx.fillRect(x + cell - t2, y, t2, t2)                     // top-right
        if (diff(row + 1, col - 1)) ctx.fillRect(x, y + cell - t2, t2, t2)                     // bottom-left
        if (diff(row + 1, col + 1)) ctx.fillRect(x + cell - t2, y + cell - t2, t2, t2)         // bottom-right
      }
    }

    // planned path overlay
    if (plannedPath.length) {
      ctx.fillStyle = C.gold; ctx.globalAlpha = 0.55
      for (const [r, c] of plannedPath) ctx.fillRect(px(c), py(r), cell, cell)
      ctx.globalAlpha = 1
    }

    // strongholds — a crenellated tower glyph. Cities get a wider base than forts, and a
    // stronghold under active siege is ringed in crimson so an investment is obvious at a
    // glance without opening the panel.
    const besiegedTiles = new Set(sieges.filter(g => g.status === 'active').map(g => `${g.row},${g.col}`))
    for (const sh of strongholds) {
      if (sh.row == null) continue
      const x = px(sh.col), y = py(sh.row)
      const w = Math.max(3, s * (sh.type === 'city' ? 0.52 : 0.38))
      const hh = Math.max(3, s * 0.46)
      const cx = x + s / 2, top = y + (s - hh) / 2
      ctx.save()
      ctx.fillStyle = C.ink
      ctx.globalAlpha = 0.85
      // body
      ctx.fillRect(cx - w / 2, top + hh * 0.28, w, hh * 0.72)
      // three merlons along the top
      const mw = w / 5
      for (let i = 0; i < 3; i++) ctx.fillRect(cx - w / 2 + i * 2 * mw, top, mw, hh * 0.3)
      ctx.globalAlpha = 1
      if (besiegedTiles.has(`${sh.row},${sh.col}`)) {
        ctx.strokeStyle = C.crimson
        ctx.lineWidth = Math.max(1, s * 0.09)
        ctx.beginPath(); ctx.arc(cx, y + s / 2, Math.max(3.5, s * 0.44), 0, Math.PI * 2); ctx.stroke()
      }
      ctx.restore()
    }

    // supply bases — a depot square outline on the tile (deactivated bases don't draw)
    for (const sb of supplyBases) {
      if (sb.tile_row == null || !sb.active) continue
      const x = px(sb.tile_col), y = py(sb.tile_row)
      const w = Math.max(4, s * 0.6), o = (s - w) / 2
      ctx.fillStyle = sb.faction_color ? muteColor(sb.faction_color) : C.inkFaint
      ctx.globalAlpha = 0.6; ctx.fillRect(x + o, y + o, w, w); ctx.globalAlpha = 1
      ctx.lineWidth = 1.5; ctx.strokeStyle = C.ink; ctx.strokeRect(x + o, y + o, w, w)
    }

    // caravans — a small diamond on the tile (distinct from army circles). A detected enemy
    // caravan (c.fog) renders faded with a dashed outline, like a fogged army.
    for (const c of caravans) {
      if (c.tile_row == null) continue
      const cx = px(c.tile_col) + s / 2, cy = py(c.tile_row) + s / 2
      const r = Math.max(2.5, s * 0.3)
      ctx.save()
      if (c.fog) ctx.globalAlpha = 0.6
      ctx.beginPath()
      ctx.moveTo(cx, cy - r); ctx.lineTo(cx + r, cy); ctx.lineTo(cx, cy + r); ctx.lineTo(cx - r, cy); ctx.closePath()
      ctx.fillStyle = c.faction_color ? muteColor(c.faction_color) : C.inkFaint; ctx.fill()
      ctx.lineWidth = 1
      if (c.fog) ctx.setLineDash([Math.max(2, s * 0.14), Math.max(2, s * 0.1)])
      ctx.strokeStyle = c.fog ? C.ink : C.paperRaised; ctx.stroke()
      ctx.restore()
    }

    // camp followers — a small tent/chevron marker (soldier-less baggage left by a detachment).
    // Own tokens are solid; a detected enemy token (cf.fog) is faded + dashed.
    for (const cf of campFollowers) {
      if (cf.tile_row == null) continue
      const cx = px(cf.tile_col) + s / 2, cy = py(cf.tile_row) + s / 2
      const r = Math.max(2.5, s * 0.32)
      ctx.save()
      if (cf.fog) ctx.globalAlpha = 0.6
      // tent: an isoceles triangle (apex up) with a base line
      ctx.beginPath()
      ctx.moveTo(cx, cy - r); ctx.lineTo(cx + r * 0.9, cy + r * 0.7); ctx.lineTo(cx - r * 0.9, cy + r * 0.7); ctx.closePath()
      ctx.fillStyle = cf.faction_color ? muteColor(cf.faction_color) : C.inkFaint; ctx.fill()
      ctx.lineWidth = 1
      if (cf.fog) ctx.setLineDash([Math.max(2, s * 0.14), Math.max(2, s * 0.1)])
      ctx.strokeStyle = C.paperRaised; ctx.stroke()
      ctx.restore()
    }

    // army markers (fogged enemies render with uncertainty: rumor = faded "?" blob, scouted/DM =
    // solid with a dashed ring; own/full armies = solid circle).
    for (const a of armies) {
      if (a.tile_row == null) continue
      const x = px(a.tile_col) + s / 2, y = py(a.tile_row) + s / 2
      const r = Math.max(3, s * 0.42)
      const rumor = a.fog && a.fog.certainty === 'rumor'
      ctx.save()
      ctx.globalAlpha = rumor ? 0.5 : 1
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2)
      ctx.fillStyle = a.faction_color ? muteColor(a.faction_color) : C.ink
      ctx.fill()
      ctx.lineWidth = a.id === selectedArmyId ? 3 : 1.5
      if (a.fog && a.id !== selectedArmyId) ctx.setLineDash([Math.max(2, s * 0.15), Math.max(2, s * 0.12)])
      ctx.strokeStyle = a.id === selectedArmyId ? C.goldBright : (a.fog ? C.ink : C.paperRaised)
      ctx.stroke()
      ctx.setLineDash([])
      if (rumor && s >= 8) {
        ctx.globalAlpha = 1; ctx.fillStyle = C.paperRaised
        ctx.font = `bold ${Math.round(r * 1.2)}px serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
        ctx.fillText('?', x, y + 0.5)
      }
      ctx.restore()
    }

    // city name labels — drawn last so they sit legibly above terrain, roads and markers.
    // Gated on zoom (below ~5px/tile the text would overlap the map); a light halo keeps
    // them readable over any terrain fill.
    if (labelTiles.length && s >= 5) {
      const fontPx = Math.max(9, Math.min(15, s * 0.6))
      ctx.font = `600 ${fontPx}px 'Cinzel', serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      ctx.lineJoin = 'round'
      ctx.lineWidth = Math.max(2, fontPx * 0.32)
      for (const [row, col, name] of labelTiles) {
        const cx = px(col) + s / 2
        const ty = py(row) + s * 0.62 + 2   // just under the city marker dot
        if (cx < -40 || ty < -20 || cx > width + 40 || ty > height + 20) continue
        ctx.strokeStyle = 'rgba(250,247,238,0.9)'   // parchment halo
        ctx.strokeText(name, cx, ty)
        ctx.fillStyle = C.ink
        ctx.fillText(name, cx, ty)
      }
    }
  }, [])

  const resize = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const parent = canvas.parentElement
    const dpr = window.devicePixelRatio || 1
    canvas.width = parent.clientWidth * dpr
    canvas.height = parent.clientHeight * dpr
    canvas.style.width = parent.clientWidth + 'px'
    canvas.style.height = parent.clientHeight + 'px'
    const v = view.current
    if (!v.initialized) {
      const fitX = canvas.width / (map.boardSize.cols + 2)
      const fitY = canvas.height / (map.boardSize.rows + 2)
      v.scale = Math.min(fitX, fitY)
      v.offsetX = (canvas.width - map.boardSize.cols * v.scale) / 2
      v.offsetY = (canvas.height - map.boardSize.rows * v.scale) / 2
      v.initialized = true
    }
    draw()
  }, [draw, map])

  useEffect(() => {
    resize()
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [resize])

  // redraw when overlays change
  useEffect(() => { draw() }, [draw, armies, caravans, campFollowers, supplyBases, strongholds, sieges, selectedArmyId, plannedPath])

  // persist + redraw when the map mode changes
  useEffect(() => { localStorage.setItem('tacitus.mapMode', mapMode); draw() }, [mapMode, draw])

  const toTile = (e) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    const cx = (e.clientX - rect.left) * dpr
    const cy = (e.clientY - rect.top) * dpr
    const v = view.current
    const col = Math.floor((cx - v.offsetX) / v.scale) + 1
    const row = Math.floor((cy - v.offsetY) / v.scale) + 1
    return { row, col }
  }

  const onWheel = (e) => {
    e.preventDefault()
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    const mx = (e.clientX - rect.left) * dpr, my = (e.clientY - rect.top) * dpr
    const v = view.current
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15
    const newScale = Math.max(1, Math.min(220, v.scale * factor))
    v.offsetX = mx - (mx - v.offsetX) * (newScale / v.scale)
    v.offsetY = my - (my - v.offsetY) * (newScale / v.scale)
    v.scale = newScale
    draw()
  }
  const onMouseDown = (e) => { drag.current = { x: e.clientX, y: e.clientY, moved: false } }
  const onMouseMove = (e) => {
    if (!drag.current) return
    const dpr = window.devicePixelRatio || 1
    const v = view.current
    const dx = e.clientX - drag.current.x, dy = e.clientY - drag.current.y
    if (Math.abs(dx) + Math.abs(dy) > 3) drag.current.moved = true
    v.offsetX += dx * dpr; v.offsetY += dy * dpr
    drag.current = { x: e.clientX, y: e.clientY, moved: drag.current.moved }
    draw()
  }
  const onMouseUp = (e) => {
    const wasDrag = drag.current && drag.current.moved
    drag.current = null
    if (!wasDrag && onTileClick) {
      const { row, col } = toTile(e)
      if (row >= 1 && col >= 1 && row <= map.boardSize.rows && col <= map.boardSize.cols) onTileClick(row, col)
    }
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', height: '100%', cursor: 'grab' }}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={() => { drag.current = null }}
      />
      {/* map-mode switcher — bottom-left corner, clear of the right panel and the centred toast */}
      <div style={{
        position: 'absolute', left: 10, bottom: 10, display: 'flex', gap: 2, padding: 3,
        background: C.paperRail, border: `1px solid ${C.ruleStrong}`, borderRadius: 6,
        boxShadow: `0 2px 8px ${C.shadow}`,
      }}>
        {MAP_MODES.map(m => {
          const on = mapMode === m.id
          return (
            <button key={m.id} onClick={() => setMapMode(m.id)} title={`${m.label} map mode`}
              style={{
                padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12,
                fontFamily: "'Cinzel', serif", letterSpacing: 0.5,
                border: `1px solid ${on ? C.gold : 'transparent'}`,
                background: on ? C.gold : 'transparent', color: on ? C.paperRaised : C.inkSoft,
              }}>
              {m.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
